import { bulkActionResponseSchema, isEmptyFilter, type BulkActionBody, type BulkTarget, type FilterDefinition } from "@gioia365/contracts";
import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ApiRequestError, apiRequest } from "../../api/http";
import { tabKey, type TabContext } from "../../session/tab-context";
import { leadCardsFilter, refreshAfterArchiveChange } from "../archive/archive-queries";

// Le azioni multiple sui Lead (piano M3 Task 14; rotta del Task 13, D10): `POST /api/leads/bulk` con l'azione, il
// bersaglio e il valore. Il server fissa il gruppo alla conferma, dentro il perimetro di modifica di chi agisce, e
// risponde quanti Lead ha cambiato, quanti erano già così e quali id scelti ha saltato. Tutto o niente: un rifiuto non
// ha scritto nulla.

/**
 * «Seleziona tutti i N risultati»: la vista di adesso con la stessa ricerca, lo stesso «Di chi» e gli stessi filtri che
 * la tabella manda a `GET /api/leads` (decisione P1), e il numero che la persona ha visto.
 */
export function queryTargetOf(view: { q: string; who: "mine" | "all" | undefined; filter: FilterDefinition }, expectedTotal: number): BulkTarget {
  return {
    kind: "query",
    ...(view.q ? { q: view.q } : {}),
    ...(view.who ? { who: view.who } : {}),
    ...(isEmptyFilter(view.filter) ? {} : { filter: view.filter }),
    expectedTotal,
  };
}

/**
 * Dopo un'azione multipla si rileggono gli stessi numeri di un cambio dell'archivio (`refreshAfterArchiveChange`: gli
 * elenchi dei Lead con il loro totale, i conteggi del menu delle Smart List e dei filtri, gli archiviati, i Lead per
 * owner della pagina Team), poi le schede e le Timeline dei Lead toccati (con «tutti i risultati» non si sa quali:
 * tutte). Un Lead archiviato non ha più una scheda (404): la sua non resta in cache.
 */
function refreshAfterBulk(queryClient: QueryClient, tab: TabContext, body: BulkActionBody): void {
  refreshAfterArchiveChange(queryClient, tab);
  const touched = leadCardsFilter(tabKey(tab), body.target.kind === "ids" ? new Set(body.target.ids) : null);
  if (body.action === "archive") queryClient.removeQueries(touched);
  else void queryClient.invalidateQueries(touched);
}

/**
 * Un'azione multipla. Riuscita, rilegge ciò che ha cambiato (`refreshAfterBulk`). Un rifiuto del server (4xx) non ha
 * scritto nulla; senza una risposta (la rete, un errore del server) non si sa, e si rilegge lo stesso.
 */
export function useBulkAction(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkActionBody) => apiRequest("POST", "/api/leads/bulk", { schema: bulkActionResponseSchema, body, tab }),
    onSuccess: (_response, body) => refreshAfterBulk(queryClient, tab, body),
    onError: (error, body) => {
      if (!(error instanceof ApiRequestError) || error.status === 0 || error.status >= 500) refreshAfterBulk(queryClient, tab, body);
    },
  });
}
